import moment from "moment";
import {
  ADVANCED_FIELD_KEYS,
  MAIN_DASHBOARD_KEYS,
  ODMSENSORHUB_CATEGORY,
} from "../constants/smartRoomConstants";
import { estimateAqiFromPm25, toNumber } from "./smartRoomStatusRanges";

const COORD_KEYS = [
  ["latitude", "longitude"],
  ["lat", "lon"],
  ["lat", "lng"],
  ["Latitude", "Longitude"],
  ["LAT", "LON"],
  ["homeLatitude", "homeLongitude"],
  ["geoLat", "geoLon"],
  ["geoLat", "geoLng"],
];

const LAT_ALIASES = new Set([
  "lat",
  "latitude",
  "homelatitude",
  "geolat",
  "y",
]);
const LON_ALIASES = new Set([
  "lon",
  "lng",
  "long",
  "longitude",
  "homelongitude",
  "geolon",
  "geolng",
  "x",
]);

export const normalizeHomeDetails = (payload) => {
  if (!payload) return null;
  if (Array.isArray(payload)) {
    return payload[0] || null;
  }
  if (payload.data && typeof payload.data === "object") {
    if (Array.isArray(payload.data)) return payload.data[0] || null;
    if (payload.data.spaces || payload.data.homeId) return payload.data;
  }
  if (payload.home && typeof payload.home === "object") {
    return payload.home;
  }
  return payload;
};

const pickLatLonFromObject = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;

  for (const [latKey, lonKey] of COORD_KEYS) {
    const lat = toNumber(obj[latKey]);
    const lon = toNumber(obj[lonKey]);
    if (lat != null && lon != null) {
      return { lat, lon };
    }
  }

  let lat;
  let lon;
  Object.keys(obj).forEach((key) => {
    const lower = key.toLowerCase();
    if (LAT_ALIASES.has(lower) && lat == null) lat = toNumber(obj[key]);
    if (LON_ALIASES.has(lower) && lon == null) lon = toNumber(obj[key]);
  });
  if (lat != null && lon != null) return { lat, lon };

  if (Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
    const lon = toNumber(obj.coordinates[0]);
    const lat = toNumber(obj.coordinates[1]);
    if (lat != null && lon != null) {
      return { lat, lon };
    }
  }

  if (Array.isArray(obj.location) && obj.location.length >= 2) {
    const lon = toNumber(obj.location[0]);
    const lat = toNumber(obj.location[1]);
    if (lat != null && lon != null) {
      return { lat, lon };
    }
  }

  for (const key of ["coordinates", "geo", "location", "latlon", "latLng"]) {
    const val = obj[key];
    if (typeof val === "string" && val.includes(",")) {
      const parts = val.split(",").map((p) => toNumber(p.trim()));
      if (parts.length >= 2 && parts[0] != null && parts[1] != null) {
        return { lat: parts[0], lon: parts[1] };
      }
    }
  }

  return null;
};

export const extractHomeCoordinates = (home) => {
  const normalized = normalizeHomeDetails(home);
  if (!normalized || typeof normalized !== "object") {
    return { lat: null, lon: null };
  }

  const queue = [
    normalized,
    normalized.address,
    normalized.location,
    normalized.geoLocation,
    normalized.geo,
    normalized.coordinates,
    normalized.homeLocation,
    normalized.position,
    normalized.mapLocation,
  ].filter(Boolean);

  const visited = new Set();
  while (queue.length) {
    const current = queue.shift();
    if (!current || typeof current !== "object") continue;
    if (visited.has(current)) continue;
    visited.add(current);

    const found = pickLatLonFromObject(current);
    if (found) return found;

    if (Array.isArray(current)) {
      if (current.length >= 2) {
        const a = toNumber(current[0]);
        const b = toNumber(current[1]);
        if (a != null && b != null) {
          return { lat: b, lon: a };
        }
      }
      current.forEach((item) => {
        if (item && typeof item === "object") queue.push(item);
      });
      continue;
    }

    Object.keys(current).forEach((key) => {
      const lower = key.toLowerCase();
      if (
        lower.includes("address") ||
        lower.includes("location") ||
        lower.includes("geo") ||
        lower.includes("coord") ||
        lower.includes("position")
      ) {
        const child = current[key];
        if (child && typeof child === "object") queue.push(child);
      }
    });
  }

  return { lat: null, lon: null };
};

export const findSpaceById = (home, spaceId) => {
  const spaces = home?.spaces;
  if (!Array.isArray(spaces) || !spaceId) return null;
  const normalized = String(spaceId).trim();
  return (
    spaces.find(
      (space) =>
        String(space?.spaceId ?? "").trim() === normalized ||
        String(space?.id ?? "").trim() === normalized
    ) || null
  );
};

const pickSpaceLocationLabel = (space) => {
  if (!space || typeof space !== "object") return "";
  if (typeof space.location === "string" && space.location.trim()) {
    return space.location.trim();
  }
  if (typeof space.address === "string" && space.address.trim()) {
    return space.address.trim();
  }
  if (space.address && typeof space.address === "object") {
    const parts = [
      space.address.line1,
      space.address.line2,
      space.address.city,
      space.address.state,
    ].filter(Boolean);
    if (parts.length) return parts.join(", ");
  }
  if (space.floor != null && space.floor !== "") {
    return `Floor ${space.floor}`;
  }
  if (typeof space.roomLocation === "string" && space.roomLocation.trim()) {
    return space.roomLocation.trim();
  }
  return "";
};

const pickSpaceType = (space) => {
  if (!space || typeof space !== "object") return "";
  return (
    space.spaceType ||
    space.type ||
    space.category ||
    space.spaceCategory ||
    space.roomType ||
    ""
  );
};

/** Normalize spaces from Home Details for the Space ID dropdown. */
export const listHomeSpaces = (home) => {
  const normalized = normalizeHomeDetails(home);
  const spaces = normalized?.spaces;
  if (!Array.isArray(spaces)) return [];

  return spaces
    .map((space) => {
      const spaceId = String(space?.spaceId ?? space?.id ?? "").trim();
      if (!spaceId) return null;
      const spaceName =
        space?.spaceName || space?.name || spaceId;
      return {
        spaceId,
        spaceName,
        spaceType: pickSpaceType(space) || "—",
        spaceLocation: pickSpaceLocationLabel(space),
        label: `${spaceName} (${spaceId})`,
        raw: space,
      };
    })
    .filter(Boolean);
};

export const getSpaceDevices = (space) => {
  if (!space || typeof space !== "object") return [];
  const list =
    space.devices ||
    space.deviceList ||
    space.device ||
    space.appliances ||
    [];
  return Array.isArray(list) ? list.filter(Boolean) : [];
};

const getDeviceId = (device) =>
  String(
    device?.deviceId ||
      device?.deviceID ||
      device?.id ||
      device?.device_id ||
      ""
  ).trim();

const getDeviceCategory = (device) =>
  String(
    device?.category ||
      device?.deviceCategory ||
      device?.categoy ||
      device?.ty ||
      device?.type ||
      ""
  )
    .trim()
    .toUpperCase();

export const isOdmsensorHubDevice = (device) => {
  if (!device || typeof device !== "object") return false;
  return getDeviceCategory(device) === ODMSENSORHUB_CATEGORY;
};

export const findOdmsensorHubInSpace = (space, enrichedDevices = []) => {
  const spaceDevices = getSpaceDevices(space);
  if (!spaceDevices.length) return null;

  const direct = spaceDevices.find((device) => isOdmsensorHubDevice(device));
  if (direct) return direct;

  if (!Array.isArray(enrichedDevices) || !enrichedDevices.length) return null;

  const byId = new Map(
    enrichedDevices.map((d) => [getDeviceId(d), d]).filter(([id]) => id)
  );

  for (const spaceDevice of spaceDevices) {
    const id = getDeviceId(spaceDevice);
    const full = byId.get(id);
    if (full && isOdmsensorHubDevice(full)) {
      return {
        ...spaceDevice,
        ...full,
        deviceId: getDeviceId(spaceDevice) || getDeviceId(full),
        deviceName:
          spaceDevice.deviceName ||
          full.deviceName ||
          full.friendlyName ||
          spaceDevice.name,
        category: ODMSENSORHUB_CATEGORY,
      };
    }
  }

  return null;
};

export const formatLocalDateTime = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const numeric = toNumber(value);
  if (numeric != null) {
    const ms = numeric < 1e12 ? numeric * 1000 : numeric;
    const m = moment(ms);
    return m.isValid() ? m.format("DD MMM YYYY, hh:mm:ss A") : "—";
  }

  const m = moment(value);
  return m.isValid() ? m.format("DD MMM YYYY, hh:mm:ss A") : String(value);
};

/**
 * Indoor "Last Updated" = last successful client refresh time.
 * Matches the 1-minute poll: do NOT use device updatedTime/ts — those often
 * stay stale between sensor reports even when status poll succeeds.
 */
export const resolveIndoorUpdatedLabel = () =>
  moment().format("DD MMM YYYY, hh:mm:ss A");

export const windDegreeToDirection = (degree) => {
  const d = toNumber(degree);
  if (d == null) return "—";
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round(((d % 360) / 45)) % 8;
  return dirs[index];
};

export const celsiusToFahrenheit = (celsius) => {
  const c = toNumber(celsius);
  if (c == null) return null;
  return (c * 9) / 5 + 32;
};

export const mapIndoorStatus = (raw = {}) => {
  const tempC = toNumber(raw.setmp);
  const tempF =
    toNumber(raw["Temp(Fer)"]) ??
    (tempC != null ? celsiusToFahrenheit(tempC) : null);

  return {
    aqiIn: toNumber(raw["AQI-IN"]),
    aqiUs: toNumber(raw["AQI-US"]),
    pm1: toNumber(raw.PM1),
    pm10: toNumber(raw.PM10),
    pm25: toNumber(raw.sepm25),
    temperatureC: tempC,
    temperatureF: tempF,
    humidity: toNumber(raw.sehum),
    co2: toNumber(raw.seco2),
    tvoc: toNumber(raw.setvoc),
    noise: toNumber(raw.Noise),
    onlineStatus: raw.onlineStatus,
    deviceName: raw.deviceName || "",
    serialNo: raw.serialNo || "",
    timeago: (raw.timeago || "").trim(),
    updatedTime: raw.updatedTime,
    connectedTime: raw.connectedTime,
    nextUpdate: raw.nextUpdate,
    ts: raw.ts,
    raw,
  };
};

export const mapWeatherStatus = (raw = {}) => {
  const temperature = toNumber(raw.temperature);
  return {
    city: raw.city || "—",
    country: raw.country || "—",
    description: raw.description || "—",
    shortDescription: raw.shortDescription || "",
    humidity: toNumber(raw.humidity),
    pm25: toNumber(raw.pm2_5),
    pressure: toNumber(raw.pressure),
    windSpeed: toNumber(raw.windSpeed),
    windDegree: toNumber(raw.windDegree),
    windDirection: windDegreeToDirection(raw.windDegree),
    sunrise: raw.sunrise,
    sunset: raw.sunset,
    temperature,
    temperatureF:
      temperature != null ? celsiusToFahrenheit(temperature) : null,
    temperatureMax: toNumber(raw.temperatureMax),
    temperatureMin: toNumber(raw.temperatureMin),
    lat: toNumber(raw.lat),
    lon: toNumber(raw.lon),
    timeStamp: raw.timeStamp,
    aqiEstimated: estimateAqiFromPm25(raw.pm2_5),
    raw,
  };
};

export const buildAdvancedRows = (raw = {}) => {
  const rows = [];
  const seen = new Set();

  ADVANCED_FIELD_KEYS.forEach(({ key, label, isTimestamp }) => {
    if (raw[key] === undefined || raw[key] === null || raw[key] === "") return;
    seen.add(key);
    rows.push({
      key,
      label,
      value: isTimestamp ? formatLocalDateTime(raw[key]) : String(raw[key]),
    });
  });

  Object.keys(raw).forEach((key) => {
    if (seen.has(key) || MAIN_DASHBOARD_KEYS.has(key)) return;
    const value = raw[key];
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "object") return;
    rows.push({ key, label: key, value: String(value) });
  });

  return rows;
};

export const buildComparisonRows = (indoor, outdoor) => {
  const makeRow = (label, indoorValue, outdoorValue, unit, higherIsWorse = false) => {
    const i = toNumber(indoorValue);
    const o = toNumber(outdoorValue);
    let difference = null;
    let arrow = "—";
    let status = "Comparable";

    if (i != null && o != null) {
      difference = Number((i - o).toFixed(1));
      if (difference > 0) {
        arrow = "↑";
        status = higherIsWorse ? "Indoor higher" : "Indoor higher";
      } else if (difference < 0) {
        arrow = "↓";
        status = "Indoor lower";
      } else {
        arrow = "=";
        status = "Same";
      }
    }

    return {
      label,
      indoorValue: i,
      outdoorValue: o,
      unit,
      difference,
      arrow,
      status,
      indoorPriority: true,
    };
  };

  return [
    makeRow("Temperature", indoor?.temperatureC, outdoor?.temperature, "°C"),
    makeRow("Humidity", indoor?.humidity, outdoor?.humidity, "%"),
    makeRow("PM2.5", indoor?.pm25, outdoor?.pm25, "µg/m³", true),
    makeRow("AQI", indoor?.aqiIn, outdoor?.aqiEstimated, "", true),
  ];
};

export const titleCaseWeather = (description = "") => {
  if (!description || description === "—") return "—";
  return description
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};
